/**
 * Vorschlagsteil — der Abschnitt „Waiting for a product or a release" in
 * Konzept B: Liste links, Detail rechts (mobil: eine Fläche, Detail als Ansicht).
 *
 * @description
 *   Ersetzt `OpenCases.jsx`. Die Zeilen der Liste sind Auswahlziele, kein
 *   Hauptknopf; die Handlung lebt im Detail (`FallDetail`). Der Abschnitt trägt
 *   GENAU EINEN Live-Zähler (`role="status"`). Die Vorschau des gewählten Falls
 *   kommt lesend aus `GET /api/second-life/vorschau`. Ohne offene Fälle nennt
 *   der Leerzustand die Zahl der übrigen Zeilen und führt zur Bestandstabelle.
 *
 * @useWhen
 *   - `/second-life` zeigt, was auf ein Produkt oder eine Freigabe wartet
 *
 * @dontUseFor
 *   - Die Bestandstabelle (bleibt eine Tabelle)
 *
 * @related
 *   - `FallListe.jsx`, `FallDetail.jsx`, `schritte.js`
 *
 * @props
 *   zeilen        ALLE Bestandszeilen (für die Zahl im Leerzustand)
 *   offene        Die offenen Fälle (`offeneFaelle(zeilen)`)
 *   onAnlegen, onFreigeben, onAblehnen, onRabatt, onKladde  — wie `OpenCases`
 *   laufend       { [sku]: art }
 *   onZeigeBestand () => … — scrollt zur Bestandstabelle (Leerzustand)
 *   onZumEditor   (zeile) => … — Text-Schritt verweist in die Bestandstabelle
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Panel from '../../components/common/Panel';
import EmptyState from '../../components/common/EmptyState';
import { SP } from '../../theme/tokens';
import { RAIL_STICKY_TOP, RAIL_STICKY_MAX_HEIGHT } from '../../components/common/detailRailSx';
import { vorschauLesen } from '../../api/secondLife';
import FallListe from './FallListe';
import FallDetail from './FallDetail';
import { vorschauLage } from './vorschauLage';

//: Die Breite der Listenspalte (Inbox-Muster, benannte Layout-Konstante wie
//: `BILD_SPALTE_PX` — kein rohes Literal im `sx`).
//: 🔴 **Eine Zahl, nicht zwei** (Orchestrator-Entscheid 1 der Nachbesserung 2,
//: 2026-11-03): die Zweispaltigkeit beginnt erst ab `lg` (1200 px). Unterhalb
//: gilt die Handyform (Liste, Tippen oeffnet das Detail als eigene Flaeche) —
//: dort gibt es keine Listenspalte, also braucht sie auch keine zweite Breite.
//: Die fruehere 280er Zwischenstufe ist entfallen; sie kuerzte die Listennamen
//: bei 1024 auf ~9 Zeichen (R16-N1) und widersprach der Doku (R16-N3).
const LISTE_BREITE_PX = 380;

export default function Vorschlagsteil({
  zeilen = [], offene = [], onAnlegen, onFreigeben, onAblehnen, onRabatt, onKladde,
  laufend = null, onZeigeBestand, onZumEditor,
}) {
  const theme = useTheme();
  // 🔴 **Ab `lg`, nicht ab `md`** (Entscheid 1 der Nachbesserung 2, 2026-11-03):
  // zwischen 900 und 1200 px war die Liste so schmal, dass die Namen auf ~9
  // Zeichen kuerzten. Unterhalb von `lg` ist es EINE Flaeche (Handyform).
  const mobil = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });

  // Auswahl: die erste Zeile der offenen Liste, deterministisch.
  const [gewaehltSku, setGewaehltSku] = useState(null);
  const [detailOffen, setDetailOffen] = useState(false);

  // Fällt der gewählte Fall aus der Liste (nach Freigabe), springt die Auswahl.
  const gewaehlt = useMemo(() => {
    if (gewaehltSku) {
      const treffer = offene.find((z) => z.sku === gewaehltSku);
      if (treffer) return treffer;
    }
    return offene[0] || null;
  }, [gewaehltSku, offene]);

  // Die Vorschau des gewählten Falls — lesend, ein A-Ruf je Wechsel UND nach
  // jedem Zustandswechsel (R15-W2): nach `onAnlegen` oder dem Speichern des
  // Textes ändern sich `state`/`desc_text`, und die alte Vorschau log dann.
  const sku = gewaehlt?.sku ?? null;
  const grade = gewaehlt?.grade ?? null;
  const zustand = gewaehlt?.state ?? null;
  const kladdeId = gewaehlt?.id ?? null;
  const entwurf = gewaehlt?.desc_text ?? null;

  // Ein Neuladen erzwingen, ohne die Abhängigkeiten zu verfälschen.
  const [neuZaehler, setNeuZaehler] = useState(0);

  const [vorschau, setVorschau] = useState(null);
  const [vorschauFehler, setVorschauFehler] = useState(null);
  // 🔴 **Der Schluessel, dessen Antwort zuletzt ANKAM** (R16-N4). Der
  // Ladezustand wird daraus ABGELEITET (`vorschauLage`) und nicht mehr im
  // `useEffect` gesetzt: der laeuft nach dem Paint, und fuer einen Frame stand
  // der Vorschauinhalt des vorigen Falls samt Knopf auf dem Schirm.
  const [fertigFuer, setFertigFuer] = useState(null);
  const schluessel = sku == null
    ? null
    : `${sku}|${grade}|${zustand}|${kladdeId}|${entwurf}|${neuZaehler}`;
  const lage = vorschauLage({
    schluessel, fertigFuer, vorschau, fehler: vorschauFehler,
  });

  useEffect(() => {
    let abgebrochen = false;
    if (!sku) {
      setVorschau(null);
      setVorschauFehler(null);
      setFertigFuer(null);
      return () => { abgebrochen = true; };
    }
    // 🔴 `Promise.resolve(...)`: ein fehlender Stellvertreter (Tests) oder ein
    // Rueckfall darf die Seite nicht reissen — die Schritte bleiben bedienbar.
    Promise.resolve(vorschauLesen?.(sku, grade))
      .then((d) => { if (!abgebrochen && d) setVorschau(d); })
      .catch(() => { if (!abgebrochen) setVorschauFehler('The preview could not be loaded. The steps still work.'); })
      .finally(() => { if (!abgebrochen) setFertigFuer(schluessel); });
    return () => { abgebrochen = true; };
  }, [schluessel]); // eslint-disable-line react-hooks/exhaustive-deps -- `schluessel` traegt alle sechs Eingaben

  const waehle = (z) => {
    setGewaehltSku(z.sku);
    if (mobil) setDetailOffen(true);
  };

  // 🔴 **S8 / §6.2: „‹ Zurück" gibt den Fokus auf die zuvor gewählte Zeile
  // zurück.** Ohne ihn landet er auf `<body>` — die Tastaturführung beginnt
  // wieder am Seitenanfang, obwohl die Auswahl sichtbar markiert ist. Der
  // Fokus wird NACH dem Rendern gesetzt (die Liste entsteht erst mit
  // `detailOffen === false`), deshalb ein Zustandsmerkmal statt eines direkten
  // `focus()` im Klick.
  const listeRef = useRef(null);
  const [zurueckFokus, setZurueckFokus] = useState(false);
  useEffect(() => {
    if (!zurueckFokus) return;
    const ziel = listeRef.current?.querySelector('[aria-current="true"]');
    ziel?.focus?.();
    setZurueckFokus(false);
  }, [zurueckFokus]);

  const n = offene.length;
  const uebrig = Math.max(0, zeilen.length - n);

  const detail = (
    <FallDetail
      zeile={gewaehlt}
      vorschau={lage.vorschau}
      vorschauFehler={lage.fehler}
      vorschauLaedt={lage.laedt}
      onVorschauNeu={() => setNeuZaehler((x) => x + 1)}
      mobil={mobil}
      onZurueck={mobil ? () => { setDetailOffen(false); setZurueckFokus(true); } : undefined}
      fokus={mobil && detailOffen}
      onAnlegen={onAnlegen}
      onFreigeben={onFreigeben}
      onAblehnen={onAblehnen}
      onRabatt={onRabatt}
      onKladde={onKladde}
      laufend={laufend}
      onZumEditor={onZumEditor}
    />
  );

  return (
    <Box component="section" sx={{ mb: SP.card }} aria-label="Waiting for a product or a release">
      {/* 🔴 **Ohne `clip`** (R15-B6): `clip` setzt `overflow: hidden`, und ein
          Vorfahr mit `overflow` ist der Scroll-Container für `position: sticky`
          — das Detail könnte dann nicht kleben. Die runden Ecken tragen die
          Kinder selbst (Zeilen und Knöpfe haben ihren eigenen Radius). */}
      <Panel pad="none" bordered shadow="panel">
        <Stack direction="row" alignItems="baseline" spacing={SP.sm} sx={{ px: SP.card, pt: SP.card, pb: SP.tight }}>
          <Typography variant="h6" component="h2">Waiting for a product or a release</Typography>
          <Typography variant="caption" color="text.secondary" role="status" sx={{ ml: 'auto' }}>
            {n === 1 ? '1 item waiting' : `${n} waiting`}
          </Typography>
        </Stack>

        {n === 0 ? (
          <Box sx={{ p: SP.card }} data-testid="vorschlag-leer">
            <EmptyState
              reason="no-match"
              message="Nothing waiting — no item is ready for a product or a release right now."
              detail={uebrig > 0
                ? `${uebrig} more item${uebrig === 1 ? ' is' : 's are'} in the stock table below; they already have a product, are published, or are scrap.`
                : undefined}
              action={uebrig > 0 && onZeigeBestand ? (
                <Button size="small" variant="outlined" onClick={onZeigeBestand}>
                  Show them in the stock table
                </Button>
              ) : undefined}
            />
          </Box>
        ) : mobil ? (
          detailOffen ? (
            <Box>{detail}</Box>
          ) : (
            <Box ref={listeRef}>
              <FallListe zeilen={offene} gewaehlt={gewaehlt?.sku || null} onWaehlen={waehle} laufend={laufend} />
            </Box>
          )
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: SP.tight }}>
            <Box sx={{ width: LISTE_BREITE_PX, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
              <FallListe zeilen={offene} gewaehlt={gewaehlt?.sku || null} onWaehlen={waehle} laufend={laufend} />
            </Box>
            {/* 🔴 **Das Detail klebt** (R15-B6, Plan §6.1): `position: sticky`
                unter der festen Kopfleiste, `alignSelf: flex-start` — sonst
                streckt sich die Box auf die Zeilenhöhe und `sticky` hat nichts
                zu greifen. Kein eigener Roller: die Seite ist der eine Roller. */}
            <Box
              data-testid="detail-klebt"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                alignSelf: 'flex-start',
                position: 'sticky',
                top: `${RAIL_STICKY_TOP}px`,
                // 🔴 **Die Hoehengrenze der Hausform** (R15-N2 / R16-N2,
                // 2026-11-03): ohne `maxHeight` + `overflowY` klebt ein Detail,
                // das hoeher ist als der Viewport, oben — und sein Fuss (die
                // Bestaetigung) ist nicht erreichbar. Beide Werte kommen aus
                // `detailRailSx.js`, nicht als zweite Zahl.
                maxHeight: RAIL_STICKY_MAX_HEIGHT,
                overflowY: 'auto',
              }}
            >
              {detail}
            </Box>
          </Box>
        )}
      </Panel>
    </Box>
  );
}
